import React from 'react';

import clsx from 'clsx';

import { isValueSet } from '@utils/guards';

import styles from './StyledInput.module.scss';

type ClassKey =
    | 'root'
    | 'label'
    | 'input'
    | 'wrapper'
    | 'placeholder'
    | 'hintMessage'
    | 'actionButton'
    | 'actionIcon';

export type StyledInputProps = {
    label?: string;
    hintMessage?: string;
    isKeepHintPlace?: boolean;
    actionIcon?: SvgIcon;
    onActionButtonClick?(): void;
    actionButtonRef?: React.Ref<HTMLButtonElement>;
    classes?: Classes<ClassKey>;
    hasError?: boolean;
    isShowPlaceholderOnFocus?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const StyledInput = React.forwardRef<HTMLInputElement, StyledInputProps>((props, ref) => {
    const {
        label,
        hintMessage,
        className,
        classes,
        placeholder,
        actionIcon: ActionIcon,
        onActionButtonClick,
        actionButtonRef,
        disabled: isDisabled,
        hasError = false,
        isKeepHintPlace = false,
        isShowPlaceholderOnFocus = false,
        ...restProps
    } = props;
    return (
        <div className={clsx(styles.root, className, classes?.root)}>
            <label
                className={clsx(
                    styles.wrapper,
                    isDisabled && styles.isDisabled,
                    hasError && styles.hasError,
                    classes?.wrapper,
                )}
            >
                <input
                    ref={ref}
                    disabled={isDisabled}
                    placeholder={
                        isValueSet(placeholder) && placeholder.length > 0 ? placeholder : label
                    }
                    {...restProps}
                    className={clsx(
                        styles.input,
                        hasError && styles.hasError,
                        isShowPlaceholderOnFocus && styles.isShowPlaceholderOnFocus,
                        isValueSet(ActionIcon) && styles.hasIcon,
                        isValueSet(label) && styles.hasLabel,
                        classes?.input,
                    )}
                />
                {isValueSet(label) && (
                    <span className={clsx(styles.label, classes?.label)}>{label}</span>
                )}
                {isValueSet(ActionIcon) && (
                    <button
                        type="button"
                        onClick={(event) => {
                            event.preventDefault();
                            if (isValueSet(onActionButtonClick)) onActionButtonClick();
                        }}
                        disabled={onActionButtonClick === undefined || isDisabled}
                        className={clsx(
                            styles.iconButton,
                            isValueSet(onActionButtonClick) && styles.hasOnClickHandler,
                            classes?.actionButton,
                        )}
                        ref={actionButtonRef}
                    >
                        <ActionIcon className={clsx(styles.icon, classes?.actionIcon)} />
                    </button>
                )}
            </label>
            <p
                className={clsx(
                    styles.hintMessage,
                    isValueSet(hintMessage) && styles.isVisible,
                    hasError && styles.isErrorMessage,
                    isKeepHintPlace && styles.isKeepHintSpace,
                    classes?.hintMessage,
                )}
            >
                {hintMessage}
            </p>
        </div>
    );
});
